import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/__session/session-context";
import downloadFile from "_shared/_utilities/download-file";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentListReadonly from "work/work-agreements/contracts/_views/document-list-readonly";
import { WorkDocumentType } from "work/work-agreements/contracts/types";
import { Masked } from "work/_shared/masked-fields";
import WorkAgreement from "work/work-agreements/work-agreement";

const ListContainer = styled(Grid)(({ theme }) => ({
  flexWrap: "nowrap",
  height: "100%",
  marginLeft: theme.spacing(3),
  marginTop: theme.spacing(2),
  minHeight: "20rem",
}));
const DocumentList = styled(DocumentListReadonly)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginTop: theme.spacing(2),
  },
  maxHeight: "60vh",
  marginLeft: theme.spacing(2),
  overflowY: "auto",
}));
const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: "1.5em",
  marginTop: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

type PoliciesTabProps = {
  workAgreement: WorkAgreement;
};

export default function PoliciesTab(props: Readonly<PoliciesTabProps>) {
  const { workAgreement } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const session = useSession();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const policyDocument = await documentApiService.downloadDocument(documentId);
      await downloadFile(policyDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to download the document. Please try again later.", {
        variant: "error",
      });
    } finally {
      setIsDownloadingFile(null);
    }
  }

  if (Masked.isMasked(workAgreement.clientPolicies) || Masked.isMasked(workAgreement.vendorPolicies)) {
    return "You do not have permission to view contracts.";
  }

  const policyGroups = [
    {
      documents: workAgreement.clientPolicies,
      documentType: WorkDocumentType.ClientPolicy,
    },
    {
      documents: workAgreement.vendorPolicies,
      documentType: WorkDocumentType.VendorPolicy,
    },
  ];
  const policyDocumentsCount = policyGroups.reduce((count, group) => count + group.documents.length, 0);

  return (
    <ListContainer container direction="row">
      <Grid>
        {policyDocumentsCount === 0 && (
          <HelperText>No policy documents</HelperText>
        )}
        {policyGroups.map((group) => (
          group.documents.length > 0 && (
            <DocumentList
              key={group.documentType}
              documents={group.documents}
              documentType={group.documentType}
              downloadingFileId={isDownloadingFile}
              hideCommentsButton={true}
              onDownload={handleDownloadDocumentById}
            />
          )
        ))}
      </Grid>
    </ListContainer>
  );
}
